import { getCombos, getFoods } from "services"
import refactorFoods from "./refactorFoods"
import { deleteFoods, removeFoodsFromCategoryGroup, store, updateCombos, updateFoods, updateFoodsInCategoryGroup } from "@redux"

const getFoodQuantityInPreviousOrder = (previousOrder, foodOptionId) => {
  if (previousOrder === null) return 0

  let result = 0

  previousOrder.items.forEach(orderItem => {
    if (orderItem.optionId !== foodOptionId) return

    result += orderItem.quantity
  })

  previousOrder.combos.forEach(orderCombo => {
    orderCombo.items.forEach(orderComboItem => {
      if (orderComboItem.optionId !== foodOptionId) return

      result += orderComboItem.quantity
    })
  })

  return result
}

const getCheckoutItems = (orderItems, orderCombos) => {
  const checkoutItemsMap = {}

  orderItems.forEach(orderItem => {
    if (!!checkoutItemsMap[orderItem.optionId]) {
      checkoutItemsMap[orderItem.optionId].push(orderItem)
    } else {
      checkoutItemsMap[orderItem.optionId] = [orderItem]
    }
  })

  orderCombos.forEach((orderCombo, orderComboIndex) =>
    orderCombo.items.forEach(orderComboItem => {
      if (!!checkoutItemsMap[orderComboItem.optionId]) {
        checkoutItemsMap[orderComboItem.optionId].push({
          orderComboIndex,
          ...orderComboItem
        })
      } else {
        checkoutItemsMap[orderComboItem.optionId] = [{
          orderComboIndex,
          ...orderComboItem
        }]
      }
    })
  )

  return checkoutItemsMap
}

const getLatestFoodsMap = async (orderItems, orderCombos) => {
  const { branchId } = store.getState().userAccountInfo

  const foodIds = []

  const comboIds = []

  orderItems.forEach(orderItem =>
    foodIds.push(orderItem.foodId)
  )

  orderCombos.forEach(orderCombo => {
    comboIds.push(orderCombo._id)

    orderCombo.items.forEach(orderComboItem =>
      foodIds.push(orderComboItem.foodId)
    )
  })

  const [response, latestCombos] =
    await Promise.all([
      getFoods({
        foodIds,
        branchId,
        limit: foodIds.length
      }),
      getCombos({ branchId })
    ])

  const latestFoods =
    refactorFoods(response, branchId)

  store.dispatch(
    updateFoodsInCategoryGroup(
      latestFoods
    )
  )

  store.dispatch(
    updateFoods(latestFoods)
  )

  store.dispatch(
    updateCombos(latestCombos)
  )

  const latestFoodsMap = {}
  latestFoods.forEach(latestFood => {
    const { options, ...foodDetails } = latestFood

    latestFood.options.forEach(option => {
      latestFoodsMap[option._id] = {
        ...foodDetails,
        option,
        isSimple: latestFood.options.length === 1
      }
    })
  })

  const latestCombosMap = {}
  latestCombos.forEach(latestCombo => {
    latestCombosMap[latestCombo._id] = latestCombo
  })

  return {
    latestFoodsMap,
    latestCombosMap
  }
}

const removeDeletedFoodsFromRedux = (checkoutItems) => {
  const foods =
    checkoutItems.map(checkoutItem => ({
      _id: checkoutItem.foodId,
      categoryIds: checkoutItem.categoryIds,
    }))

  store.dispatch(
    deleteFoods(foods)
  )

  store.dispatch(
    removeFoodsFromCategoryGroup(foods)
  )
}

const isFoodOutOfStock = (
  latestFood,
  checkoutItems
) => {
  const result = []

  if (!latestFood) {
    checkoutItems.forEach(checkoutItem => {
      const deletedFood = {
        type: "deletedFood",
        unit: checkoutItem.unit,
        isSimple: checkoutItem.isSimple,
        foodName: checkoutItem.foodName,
        optionName: checkoutItem.optionName,
      }

      result.push(deletedFood)
    })

    // remove food from section list
    removeDeletedFoodsFromRedux(
      checkoutItems
    )

    return result
  }

  const isOutOfStock =
    latestFood.option.isAvailable
      ?
      latestFood.option.outOfStock ||
      (
        latestFood.option.stockManagement &&
        latestFood.option.availableQuantity <= 0
      )
      :
      true

  if (isOutOfStock) {
    checkoutItems.forEach(checkoutItem => {
      const deletedFood = {
        type: "deletedFood",
        unit: checkoutItem.unit,
        isSimple: checkoutItem.isSimple,
        foodName: checkoutItem.foodName,
        optionName: checkoutItem.optionName,
      }

      result.push(deletedFood)
    })

    return result
  }

  return result
}

const isFoodStockLimited = (
  latestFood,
  checkoutItems,
  previousOrder
) => {
  const result = []

  if (!latestFood.option.stockManagement)
    return result

  const availableQuantity =
    latestFood.option.availableQuantity

  const foodOptionId =
    latestFood.option._id

  const totalFoodQuantityInCart =
    checkoutItems.reduce((t, checkoutItem) =>
      t += checkoutItem.quantity
      , 0
    )

  const foodQuantityInPreviousOrder =
    getFoodQuantityInPreviousOrder(
      previousOrder,
      foodOptionId
    )

  const neededFoodQuantity =
    totalFoodQuantityInCart -
    foodQuantityInPreviousOrder

  if (neededFoodQuantity > availableQuantity) {
    result.push({
      type: "limitedFood",
      unit: latestFood.unit,
      foodName: latestFood.name,
      isSimple: latestFood.isSimple,
      optionName: latestFood.option.name,
      stockManagement: latestFood.option.stockManagement,
      availableQuantity: availableQuantity
    })
  }

  return result
}

const isFoodPriceChanged = (latestFood, checkoutItems) => {
  const result = []

  const foodPriceHasChanged =
    checkoutItems.some(checkoutItem =>
      checkoutItem.salePrice !==
      latestFood.option.salePrice
    )

  if (!foodPriceHasChanged) return result

  checkoutItems.forEach(checkoutItem => {
    const foodPriceChanged = {
      type: "foodPriceChanged",
      isSimple: checkoutItem.isSimple,
      foodName: checkoutItem.foodName,
      optionId: checkoutItem.optionId,
      optionName: checkoutItem.optionName,
      previousSalePrice: checkoutItem.salePrice,
      newSalePrice: latestFood.option.salePrice,
      stockManagement: latestFood.option.stockManagement,
      availableQuantity: latestFood.option.availableQuantity
    }

    result.push(foodPriceChanged)
  })

  return result
}

const isFoodTaxChanged = (latestFood, checkoutItems) => {
  const result = []

  checkoutItems.forEach(checkoutItem => {
    if (
      checkoutItem.taxItems.length === 0
      &&
      latestFood.taxItems.length === 0
    ) return

    const checkoutItemTaxItemsMap = {}

    const newFoodTaxItemsMap = {}

    checkoutItem.taxItems.forEach(taxItem =>
      checkoutItemTaxItemsMap[taxItem._id] = taxItem
    )

    latestFood.taxItems.forEach(taxItem =>
      newFoodTaxItemsMap[taxItem._id] = taxItem
    )

    const foodTaxChanged = {
      type: "foodTaxChanged",
      isSimple: checkoutItem.isSimple,
      foodName: checkoutItem.foodName,
      optionId: checkoutItem.optionId,
      optionName: checkoutItem.optionName,
      newTaxItems: latestFood.taxItems,
      stockManagement: latestFood.option.stockManagement,
      availableQuantity: latestFood.option.availableQuantity
    }

    const taxRemoved =
      checkoutItem.taxItems.some(taxItem =>
        !!newFoodTaxItemsMap[taxItem._id]
      )

    if (!taxRemoved) {
      return result.push(foodTaxChanged)
    }

    const newTaxAdded =
      latestFood.taxItems.some(taxItem =>
        !!checkoutItemTaxItemsMap[taxItem._id]
      )

    if (!newTaxAdded) {
      return result.push(foodTaxChanged)
    }

    const taxChanged =
      checkoutItem.taxItems.some(taxItem =>
        taxItem.percent === newFoodTaxItemsMap[taxItem._id].percent
        &&
        taxItem.isNotTax === newFoodTaxItemsMap[taxItem._id].isNotTax
        &&
        taxItem.constantValue === newFoodTaxItemsMap[taxItem._id].constantValue
        &&
        taxItem.isPercentageModel === newFoodTaxItemsMap[taxItem._id].isPercentageModel
      )

    if (!taxChanged) {
      return result.push(foodTaxChanged)
    }
  })

  return result
}

const isComboPriceChanged = (
  latestCombo,
  orderCombo,
  orderComboIndex
) => {
  const result = []

  const comboPriceChanged =
    orderCombo.comboSetSalePrice !==
    latestCombo.comboSetSalePrice

  if (comboPriceChanged) {
    result.push({
      orderComboIndex,
      comboName: orderCombo.name,
      type: "comboPriceChanged",
      previousComboSetSalePrice: orderCombo.comboSetSalePrice,
      newComboSetSalePrice: latestCombo.comboSetSalePrice
    })
  }

  return result
}

const isComboStillEligible = (latestCombo, orderCombo) => {
  let isEligibale = true

  latestCombo.isBuyxFreeX
    ?
    latestCombo.comboSets.forEach(comboSet => {
      if (comboSet.isFixed) {
        comboSet.items.forEach(comboSetItem => {
          const targetComboItem =
            orderCombo.items.find(orderComboItem =>
              orderComboItem.optionId === comboSetItem.optionId
            )

          if (!targetComboItem)
            return isEligibale = false

          if (targetComboItem.quantity !== comboSetItem.quantity)
            return isEligibale = false
        })

        return
      }

      let sum = 0

      comboSet.items.forEach(comboSetItem => {
        const targetComboItem =
          orderCombo.items.find(orderComboItem =>
            orderComboItem.optionId === comboSetItem.optionId
          )

        if (!targetComboItem) return

        sum += targetComboItem.quantity
      })

      if (sum !== comboSet.maxQty) isEligibale = false
    })
    :
    latestCombo.comboSets.forEach(comboSet => {
      const somethingMissing =
        !comboSet.items.some(comboSetItem => (
          orderCombo.items.some(orderComboItem =>
            orderComboItem.optionId === comboSetItem.optionId &&
            orderComboItem.quantity === comboSetItem.quantity
          )
        ))

      if (somethingMissing) isEligibale = false
    })

  const result =
    !isEligibale
      ?
      {
        comboName: orderCombo.name,
        type: "comboNotEligable"
      }
      :
      null

  return result
}

const isFoodSpecificationItemsChanged = (latestFood, checkoutItems) => {
  const result = []

  checkoutItems.forEach(checkoutItem => {
    if (!checkoutItem.specificationItems) return

    checkoutItem.specificationItems.forEach(specificationItem => {
      let targetSpecificationItemDetails = null

      latestFood.specifications.forEach(specification => {
        specification.specificationItems.forEach(si => {
          if (si._id !== specificationItem._id) return

          targetSpecificationItemDetails = {
            groupName: specification.groupName,
            maxQuantity: specification.maxQuantity,
            ...si
          }
        })
      })

      if (!targetSpecificationItemDetails) {
        const deletedSpecificationItem = {
          type: "outOfStockSpecificationItem",
          foodName: checkoutItem.foodName,
          specificationItemName: specificationItem.name,
          specificationItemId: specificationItem._id
        }

        result.push(deletedSpecificationItem)

        return
      }

      const isOutOfStock =
        targetSpecificationItemDetails.isAvailable

      if (!isOutOfStock) {
        const deletedSpecificationItem = {
          type: "outOfStockSpecificationItem",
          foodName: checkoutItem.foodName,
          specificationItemName: specificationItem.name,
          specificationItemId: specificationItem._id
        }

        result.push(deletedSpecificationItem)

        return
      }

      const priceChanged =
        targetSpecificationItemDetails.salePrice !==
        specificationItem.salePrice

      if (priceChanged) {
        const changedPriceSpecificationItem = {
          type: "changedPriceSpecificationItem",
          foodName: checkoutItem.foodName,
          specificationItemName: specificationItem.name,
          previousSalePrice: specificationItem.salePrice,
          newSalePrice: targetSpecificationItemDetails.salePrice,
          optionId: checkoutItem.optionId,
          specificationItemId: specificationItem._id
        }

        result.push(changedPriceSpecificationItem)

        return
      }

    })
  })

  return result
}

const validateCartContents = async (orderItems, orderCombos, previousOrder = null) => {
  const {
    latestFoodsMap,
    latestCombosMap
  } = await getLatestFoodsMap(orderItems, orderCombos)

  const checkoutItemsMap =
    getCheckoutItems(orderItems, orderCombos)

  let problematicItems = []

  const optionIds =
    Object.keys(checkoutItemsMap)

  optionIds.forEach(optionId => {
    const latestFood = latestFoodsMap[optionId]

    const outOfStockItems =
      isFoodOutOfStock(
        latestFood,
        checkoutItemsMap[optionId]
      )

    if (outOfStockItems.length > 0) {
      problematicItems =
        [...outOfStockItems, ...problematicItems]

      return
    }

    const limitedStockItems =
      isFoodStockLimited(
        latestFood,
        checkoutItemsMap[optionId],
        previousOrder
      )

    problematicItems =
      [...limitedStockItems, ...problematicItems]

    const taxChangedItems =
      isFoodTaxChanged(
        latestFood,
        checkoutItemsMap[optionId]
      )

    problematicItems =
      [...taxChangedItems, ...problematicItems]

    const changedPricedItems =
      isFoodPriceChanged(
        latestFood,
        checkoutItemsMap[optionId],
      )

    problematicItems =
      [...changedPricedItems, ...problematicItems]

    const changedSpecificationItems =
      isFoodSpecificationItemsChanged(
        latestFood,
        checkoutItemsMap[optionId],
      )

    problematicItems =
      [...changedSpecificationItems, ...problematicItems]
  })

  orderCombos.forEach((orderCombo, orderComboIndex) => {
    const latestCombo = latestCombosMap[orderCombo._id]

    if (!latestCombo) {
      problematicItems.push({
        comboName: orderCombo.name,
        type: "comboDeleted"
      })

      return
    }

    const changedPriceCombos =
      isComboPriceChanged(
        latestCombo,
        orderCombo,
        orderComboIndex
      )

    problematicItems =
      [...changedPriceCombos, ...problematicItems]

    const ineligibleCombos =
      isComboStillEligible(
        latestCombo,
        orderCombo
      )

    !!ineligibleCombos &&
      problematicItems.unshift(ineligibleCombos)
  })

  return problematicItems
}

export default validateCartContents